import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Eye, TrendingUp, Clock, CheckCircle, XCircle, Loader2, Gift } from 'lucide-react';
import { Avatar, toast } from '@clareefai/ui';
import { useGetSpecificRewardCommitmentsQuery, useMarkSpecificRewardPaidMutation } from '../../../store/vendorApi';
import CommitmentTaskBadge from './CommitmentTaskBadge';
import { Can } from '../../../contexts/AbilityContext';
import type { CommitmentStatus } from '@clareefai/types';

const STATUS_COLORS: Record<CommitmentStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CLAIMED: 'bg-purple-100 text-purple-800',
  EXPIRED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
  REWARD_PAID: 'bg-emerald-100 text-emerald-800',
};

const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };

const getDiscountLabel = (unit: string | null, amount: number | null) => {
  if (!unit || !amount) return '';
  switch (unit) {
    case 'percentage': return `${amount}%`;
    case 'credits': return `${amount} credits`;
    case 'free_months': return `${amount} mo.`;
    default: return `${amount}`;
  }
};

const getRewardLabel = (item: { reward_method: string; gift_card_amount: number | null; gift_card_currency: string | null; discount_unit: string | null; discount_amount: number | null }) => {
  const parts: string[] = [];
  if ((item.reward_method === 'GIFT_CARD' || item.reward_method === 'BOTH') && item.gift_card_amount) {
    parts.push(`${item.gift_card_amount}${CURRENCY_SYMBOLS[item.gift_card_currency || 'EUR'] || '€'}`);
  }
  if ((item.reward_method === 'DISCOUNT' || item.reward_method === 'BOTH') && item.discount_amount) {
    parts.push(getDiscountLabel(item.discount_unit, item.discount_amount));
  }
  return parts.join(' / ') || '—';
};

const SpecificRewardsListTab: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: commitments = [], isLoading } = useGetSpecificRewardCommitmentsQuery();
  const [markPaid, { isLoading: isMarkingPaid }] = useMarkSpecificRewardPaidMutation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const STATUS_CONFIG: Record<CommitmentStatus, { label: string; color: string }> = {
    PENDING: { label: t('rewards.specific.list.statusPending'), color: STATUS_COLORS.PENDING },
    IN_PROGRESS: { label: t('rewards.specific.list.statusInProgress'), color: STATUS_COLORS.IN_PROGRESS },
    COMPLETED: { label: t('rewards.specific.list.statusCompleted'), color: STATUS_COLORS.COMPLETED },
    CLAIMED: { label: t('rewards.specific.list.statusClaimed'), color: STATUS_COLORS.CLAIMED },
    EXPIRED: { label: t('rewards.specific.list.statusExpired'), color: STATUS_COLORS.EXPIRED },
    CANCELLED: { label: t('rewards.specific.list.statusCancelled'), color: STATUS_COLORS.CANCELLED },
    REWARD_PAID: { label: t('rewards.specific.list.statusRewardPaid'), color: STATUS_COLORS.REWARD_PAID },
  };

  const handleMarkPaid = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await markPaid(id).unwrap();
      toast.success(t('rewards.specific.list.markedPaid'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('rewards.specific.list.failedToMarkPaid'));
    }
  };

  const filtered = commitments.filter((c) => {
    const matchesSearch = !searchQuery ||
      (c.promoter_name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      c.promoter_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.promoter_company?.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: commitments.length,
    pending: commitments.filter((c) => c.status === 'PENDING').length,
    inProgress: commitments.filter((c) => c.status === 'IN_PROGRESS').length,
    completed: commitments.filter((c) => c.status === 'COMPLETED' || c.status === 'REWARD_PAID').length,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#4339A0]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4339A0]/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#4339A0]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">{t('common.total')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              <p className="text-xs text-gray-500">{t('common.pending')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p>
              <p className="text-xs text-gray-500">{t('common.inProgress')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
              <p className="text-xs text-gray-500">{t('common.completed')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('rewards.specific.list.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
          >
            <option value="all">{t('rewards.specific.list.allStatus')}</option>
            <option value="PENDING">{t('rewards.specific.list.statusPending')}</option>
            <option value="IN_PROGRESS">{t('rewards.specific.list.statusInProgress')}</option>
            <option value="COMPLETED">{t('rewards.specific.list.statusCompleted')}</option>
            <option value="CLAIMED">{t('rewards.specific.list.statusClaimed')}</option>
            <option value="REWARD_PAID">{t('rewards.specific.list.statusRewardPaid')}</option>
            <option value="EXPIRED">{t('rewards.specific.list.statusExpired')}</option>
            <option value="CANCELLED">{t('rewards.specific.list.statusCancelled')}</option>
          </select>
        </div>
        <Can I="create" a="Reward">
          <button
            onClick={() => navigate('/rewards/add-specific')}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> {t('rewards.specific.list.addSpecificReward')}
          </button>
        </Can>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">{t('rewards.specific.list.noRewardsFound')}</h3>
          <p className="text-sm text-gray-500 mb-4">{t('rewards.specific.list.noRewardsDesc')}</p>
          <Can I="create" a="Reward">
            <button
              onClick={() => navigate('/rewards/add-specific')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> {t('rewards.specific.list.createSpecificReward')}
            </button>
          </Can>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {filtered.map((item) => {
            const statusCfg = STATUS_CONFIG[item.status];
            const progressPct = item.tasks_total > 0 ? Math.round((item.tasks_completed / item.tasks_total) * 100) : 0;
            const isOverdue = new Date(item.deadline) < new Date() && item.status !== 'COMPLETED' && item.status !== 'CLAIMED' && item.status !== 'REWARD_PAID' && item.status !== 'CANCELLED';
            const canMarkPaid = item.status === 'COMPLETED' || item.status === 'CLAIMED';

            return (
              <div key={item.id} className="px-5 py-4">
                {/* Line 1: Avatar + Name/Company + Reward + Status + Actions */}
                <div className="flex items-center gap-3">
                  <Avatar src={item.promoter_avatar} name={item.promoter_name || item.promoter_email} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900 truncate">{item.promoter_name || t('common.unknown')}</span>
                      {item.promoter_company && (
                        <span className="text-xs text-gray-400 truncate hidden sm:inline">· {item.promoter_company}</span>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{getRewardLabel(item)}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusCfg.color}`}>
                    {statusCfg.label}
                  </span>
                  <div className="flex items-center gap-2 ml-1">
                    {canMarkPaid && (
                      <button
                        onClick={() => handleMarkPaid(item.id, { stopPropagation: () => {} } as React.MouseEvent)}
                        disabled={isMarkingPaid}
                        className="inline-flex items-center gap-1 text-xs text-white bg-green-600 hover:bg-green-700 px-2.5 py-1 rounded-md font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        <Gift className="w-3 h-3" /> {t('rewards.specific.list.markPaid')}
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/rewards/specific/${item.id}`)}
                      className="inline-flex items-center gap-1 text-xs text-[#4339A0] hover:text-[#3d2f8f] font-medium whitespace-nowrap"
                    >
                      <Eye className="w-3.5 h-3.5" /> {t('common.view')}
                    </button>
                  </div>
                </div>
                {/* Line 2: Tasks + Progress bar + Deadline */}
                <div className="flex items-center gap-4 mt-2 ml-11">
                  <span className="text-xs text-gray-500 truncate max-w-[220px]">{item.tasks_summary}</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#4339A0] rounded-full" style={{ width: `${progressPct}%` }} />
                    </div>
                    <span className="text-xs text-gray-500">{item.tasks_completed}/{item.tasks_total}</span>
                  </div>
                  <span className={`text-xs whitespace-nowrap ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                    {t('rewards.specific.list.due', { date: new Date(item.deadline).toLocaleDateString() })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpecificRewardsListTab;
